/**
 * DUCS wire protocol — shared verbatim between the browser client and the
 * authoritative Node server. Both sides `import` this file, so a field added
 * here exists on both or neither.
 *
 * Transport: WebSocket. Control messages are JSON (`{t: MSG.x, ...}`). Input
 * and snapshot messages are JSON too, with fixed short keys and 3-decimal
 * rounded floats — 10 players at 30 Hz is ~45 kB/s, which is not the
 * bottleneck. The encoding is isolated behind packInput/packSnapshot so it can
 * be swapped for binary without touching callers.
 *
 * Timing model
 * ------------
 *   server sim   120 Hz, one tick == FIXED_DT, identical to the client's
 *                fixedUpdate. The server runs the same Movement class against
 *                the same level, so prediction converges exactly.
 *   client input one command per fixed step, batched ~2 per packet at 60 Hz.
 *   snapshots    30 Hz, delta-free full state (10 players is small).
 */

export const PROTOCOL_VERSION = 1;

/** Message types. */
export const MSG = {
  /* client -> server */
  HELLO: 'hello',           // { name, mode? }              join request
  INPUT: 'input',           // { seq, cmds: [Cmd, ...] }    batched input
  CHAT: 'chat',             // { text }
  BUY: 'buy',               // { item }                     economy purchase
  TEAM: 'team',             // { team }                     request team slot
  READY: 'ready',           // {}                           leave warmup
  PING: 'ping',             // { t }

  /* server -> client */
  WELCOME: 'welcome',       // { id, tick, snapshot, match, players }
  SNAPSHOT: 'snap',         // { tick, t, players, events, match }
  EVENT: 'event',           // { kind, ... }                discrete game event
  PONG: 'pong',             // { t, tick }
  REJECT: 'reject',         // { reason }
};

/** Input button bits. Move axes and angles are separate fields. */
export const BUTTON = {
  JUMP: 1 << 0,
  CROUCH: 1 << 1,
  PRONE: 1 << 2,
  SPRINT: 1 << 3,
  LEAN_L: 1 << 4,
  LEAN_R: 1 << 5,
  ADS: 1 << 6,
  FIRE: 1 << 7,
  RELOAD: 1 << 8,
  USE: 1 << 9,          // plant / defuse / pick up
  WALK: 1 << 10,
  DROP: 1 << 11,
  MELEE: 1 << 12,
  SWAP: 1 << 13,
};

/** Server-authoritative discrete events the client reacts to. */
export const EV = {
  JOIN: 'join',
  LEAVE: 'leave',
  KILL: 'kill',           // { killer, victim, weapon, headshot, wallbang }
  DAMAGE: 'damage',       // { target, amount, from, headshot }
  FIRE: 'fire',           // { who, weapon }
  HIT_CONFIRM: 'hitconf', // { target, headshot, killed }   only the shooter gets this
  IMPACT: 'impact',       // { x,y,z, nx,ny,nz, surface }
  RELOAD: 'reload',       // { who }
  ROUND_START: 'roundStart',
  ROUND_END: 'roundEnd',  // { winner, reason }
  BOMB_PLANTED: 'bombPlanted',
  BOMB_DEFUSED: 'bombDefused',
  BOMB_DROPPED: 'bombDropped',
  EXPLODE: 'explode',
  MATCH_END: 'matchEnd',
  TEAM_SWAP: 'teamSwap',
  BUY_OK: 'buyOk',
  BUY_FAIL: 'buyFail',
  MONEY: 'money',
  CHAT: 'chat',
};

/** Teams — Counter-Strike naming. */
export const TEAM = { NONE: 0, T: 1, CT: 2, SPECTATOR: 3 };
export const TEAM_NAME = { 0: 'none', 1: 'TERRORIST', 2: 'COUNTER-TERRORIST', 3: 'spectator' };

/** Round end reasons, surfaced in the HUD. */
export const ROUND_END = {
  ELIMINATION: 'elimination',
  BOMB_DETONATED: 'bombDetonated',
  BOMB_DEFUSED: 'bombDefused',
  TIME: 'time',
};

// Wire quantisation. Snapshots are 10 players × 30 Hz of JSON, so every digit
// here is paid for many times over on a phone connection.
/** 2 decimals = 1 cm. Sub-centimetre position is invisible at these ranges. */
const q = (n) => Math.round(n * 100) / 100;
/** 3 decimals ≈ 0.06° — well under what a rendered model can show. */
const qa = (n) => Math.round(n * 1000) / 1000;

/* ─────────────────────────── input commands ─────────────────────────── */

/**
 * One fixed-step input command. `tick` is the client's predicted tick number
 * this command is for — the server uses it for lag compensation and the client
 * uses it to reconcile.
 */
export function makeCmd() {
  return { tick: 0, mx: 0, my: 0, yaw: 0, pitch: 0, buttons: 0, aim: 0 };
}

export function packCmd(c) {
  const o = { t: c.tick, x: q(c.mx), y: q(c.my), w: qa(c.yaw), p: qa(c.pitch), b: c.buttons };
  // Only shots need a rewind target; omitting it keeps idle traffic small.
  if (c.aim) o.a = c.aim | 0;
  return o;
}

export function unpackCmd(o, out = makeCmd()) {
  out.tick = o.t | 0;
  out.mx = o.x || 0;
  out.my = o.y || 0;
  out.yaw = o.w || 0;
  out.pitch = o.p || 0;
  out.buttons = o.b | 0;
  out.aim = o.a | 0;
  return out;
}

/* ─────────────────────────── snapshots ─────────────────────────── */

/**
 * Per-player snapshot record. Deliberately flat and short-keyed: this is the
 * hot path, 10 of these 30 times a second.
 */
export function packPlayer(p) {
  const o = {
    i: p.id,
    /* feet position */
    x: q(p.x), y: q(p.y), z: q(p.z),
    /* view angles */
    w: qa(p.yaw), p: qa(p.pitch),
    /* state */
    hp: p.hp | 0,
    ap: p.armor | 0,
    tm: p.team | 0,
    al: p.alive ? 1 : 0,
    st: p.stance | 0,          // 0 stand 1 crouch 2 prone
    sl: p.sliding ? 1 : 0,
    ad: Math.round(p.ads * 255),
    wp: p.weapon | 0,
    am: p.ammo | 0,
    rs: p.reloading ? 1 : 0,
    sd: q(p.sound),            // footstep/fire loudness, for audio occlusion
  };
  // Velocity is for remote extrapolation only. Most players are standing still
  // most of the time, so omit the zeros — readers already do `rec.vx ?? 0`.
  const vx = q(p.vx), vy = q(p.vy), vz = q(p.vz);
  if (vx) o.vx = vx;
  if (vy) o.vy = vy;
  if (vz) o.vz = vz;
  return o;
}

export function unpackPlayer(o, out = {}) {
  out.id = o.i | 0;
  out.x = o.x; out.y = o.y; out.z = o.z;
  out.vx = o.vx; out.vy = o.vy; out.vz = o.vz;
  out.yaw = o.w; out.pitch = o.p;
  out.hp = o.hp | 0;
  out.armor = o.ap | 0;
  out.team = o.tm | 0;
  out.alive = o.al === 1;
  out.stance = o.st | 0;
  out.sliding = o.sl === 1;
  out.ads = (o.ad | 0) / 255;
  out.weapon = o.wp | 0;
  out.ammo = o.am | 0;
  out.reloading = o.rs === 1;
  out.sound = o.sd || 0;
  return out;
}

/* ─────────────────────────── weapons / economy ─────────────────────────── */

/**
 * The buy catalogue. Damage/penetration values are shared with the client so
 * the HUD can show honest numbers and the server stays authoritative on both.
 *
 * `rpm` is rounds/min, `dmg` base damage at the muzzle, `pen` the ballistic
 * penetration budget (see src/physics/penetration.js), `armor` the fraction of
 * damage armour absorbs, `spread` the base cone in radians.
 */
export const WEAPONS = [
  { id: 0, key: 'knife',     name: 'Tactical Knife', team: 0, cost: 0,    kind: 'melee',  rpm: 120, dmg: 55, pen: 0.05, armor: 0.5, spread: 0,     mag: 0,  reserve: 0,   reload: 0,   move: 1.0,  recoil: 0 },
  { id: 1, key: 'glock',     name: 'Glock-18',       team: 1, cost: 200,  kind: 'pistol', rpm: 400, dmg: 28, pen: 0.35, armor: 0.5, spread: 0.010, mag: 20, reserve: 120, reload: 2.2, move: 1.0,  recoil: 0.55 },
  { id: 2, key: 'usp',       name: 'USP-S',          team: 2, cost: 200,  kind: 'pistol', rpm: 352, dmg: 32, pen: 0.35, armor: 0.5, spread: 0.009, mag: 12, reserve: 96,  reload: 2.2, move: 1.0,  recoil: 0.5 },
  { id: 3, key: 'deagle',    name: 'Desert Eagle',   team: 0, cost: 700,  kind: 'pistol', rpm: 267, dmg: 68, pen: 0.9,  armor: 0.9, spread: 0.014, mag: 7,  reserve: 35,  reload: 2.6, move: 0.96, recoil: 1.5 },
  { id: 4, key: 'p90',       name: 'P90',            team: 0, cost: 2350, kind: 'smg',    rpm: 857, dmg: 26, pen: 0.5,  armor: 0.6, spread: 0.016, mag: 50, reserve: 100, reload: 3.0, move: 0.98, recoil: 0.8 },
  { id: 5, key: 'mp9',       name: 'MP9',            team: 2, cost: 1250, kind: 'smg',    rpm: 857, dmg: 26, pen: 0.5,  armor: 0.6, spread: 0.015, mag: 30, reserve: 120, reload: 2.1, move: 1.0,  recoil: 0.75 },
  { id: 6, key: 'mac10',     name: 'MAC-10',         team: 1, cost: 1050, kind: 'smg',    rpm: 857, dmg: 27, pen: 0.5,  armor: 0.6, spread: 0.017, mag: 30, reserve: 100, reload: 2.1, move: 1.0,  recoil: 0.85 },
  { id: 7, key: 'ak47',      name: 'AK-47',          team: 1, cost: 2700, kind: 'rifle',  rpm: 600, dmg: 42, pen: 1.2,  armor: 0.78, spread: 0.007, mag: 30, reserve: 90,  reload: 2.4, move: 0.93, recoil: 1.35 },
  { id: 8, key: 'm4a4',      name: 'M4A4',           team: 2, cost: 2900, kind: 'rifle',  rpm: 666, dmg: 38, pen: 1.1,  armor: 0.7, spread: 0.006, mag: 30, reserve: 90,  reload: 3.0, move: 0.94, recoil: 1.15 },
  { id: 9, key: 'awp',       name: 'AWP',            team: 0, cost: 4750, kind: 'sniper', rpm: 41,  dmg: 132, pen: 2.0, armor: 0.95, spread: 0.002, mag: 10, reserve: 30,  reload: 3.6, move: 0.82, recoil: 2.6 },
  { id: 10, key: 'nova',     name: 'Nova',           team: 0, cost: 1050, kind: 'shotgun', rpm: 68, dmg: 24, pen: 0.4, armor: 0.5, spread: 0.045, mag: 8,  reserve: 32,  reload: 4.0, move: 0.95, recoil: 1.8, pellets: 9 },
];

export const WEAPON_BY_KEY = Object.fromEntries(WEAPONS.map((w) => [w.key, w]));
export const weaponById = (id) => WEAPONS[id] ?? WEAPONS[0];

/** Non-weapon purchases. */
export const GEAR = [
  { key: 'kevlar',  name: 'Kevlar',        cost: 650,  team: 0 },
  { key: 'helmet',  name: 'Kevlar+Helmet', cost: 1000, team: 0, needs: 'kevlar' },
  { key: 'defuse',  name: 'Defuse Kit',    cost: 400,  team: 2 },
  { key: 'he',      name: 'HE Grenade',    cost: 300,  team: 0 },
  { key: 'flash',   name: 'Flashbang',     cost: 200,  team: 0 },
  { key: 'smoke',   name: 'Smoke Grenade', cost: 300,  team: 0 },
];
export const GEAR_BY_KEY = Object.fromEntries(GEAR.map((g) => [g.key, g]));

/** Damage multipliers by hitbox part. Headshots are the whole point. */
export const PART_MULT = { head: 4.0, chest: 1.0, stomach: 1.25, legs: 0.75, arms: 1.0 };

/** Economy constants (CS:GO values). */
export const ECONOMY = {
  startMoney: 800,
  maxMoney: 16000,
  pistolRoundMoney: 800,
  killReward: 300,
  headshotBonus: 100,
  bombPlantReward: 300,
  bombDefuseReward: 300,
  /** loss bonus ladder, index = consecutive losses (capped) */
  lossBonus: [1400, 1900, 2400, 2900, 3400],
  winBonusElimination: 3250,
  winBonusBomb: 3500,
  winBonusTime: 3250,
};

export const STANCE = { STAND: 0, CROUCH: 1, PRONE: 2 };

/** Hitbox layout in metres, relative to the feet. Mirrors src/player/tuning.js. */
export const HITBOX = {
  head: { r: 0.115, y: 1.62 },
  chest: { y0: 1.05, y1: 1.48, r: 0.20 },
  stomach: { y0: 0.86, y1: 1.05, r: 0.19 },
  legs: { y0: 0.10, y1: 0.86, r: 0.17 },
};
